import {
  deriveTicketState,
  ticketTypeOf,
} from 'dashboard/helper/conversationLens';

/**
 * Reads a conversation from the shared `conversations` store as a ticket row.
 *
 * The conversation payload is the only source of truth here - nothing is
 * cached or recomputed - so a ticket cannot drift from the same conversation
 * rendered in the chat inbox.
 *
 * @param {Object} conversation - snake_case conversation from the store
 * @param {Function} getPlainText - strips markup from message content
 * @returns {Object} ticket view model
 */
export const presentTicket = (conversation, getPlainText) => {
  const {
    id,
    priority,
    subject,
    labels = [],
    unread_count: unreadCount = 0,
    timestamp,
    created_at: createdAt,
    additional_attributes: additionalAttributes = {},
    last_non_activity_message: lastMessage,
    waiting_since: waitingSince = 0,
    meta: { sender = {}, assignee, channel, team } = {},
  } = conversation;

  const preview = getPlainText(lastMessage?.content || '');

  return {
    id,
    conversation,
    // `subject` is a real column now, but conversations that predate it (and
    // every chat-origin conversation) fall back the way the design does: the
    // mail subject, then the first line of the latest message.
    subject: subject || additionalAttributes.mail_subject || preview,
    preview,
    // The whole conversation goes in so the server-provided `ticket_state`
    // wins over the local status x resolution fallback.
    state: deriveTicketState(conversation),
    type: ticketTypeOf(conversation),
    priority,
    labels,
    unread: unreadCount > 0,
    lastActivityAt: timestamp,
    // `waiting_since` serializes as 0 when nobody is waiting, so a falsy value
    // reads as "not waiting" rather than as the epoch.
    waitingSince: waitingSince || null,
    createdAt,
    source: channel,
    team: team ? { id: team.id, name: team.name } : null,
    assignee: assignee
      ? {
          id: assignee.id,
          name: assignee.name || assignee.available_name,
          thumbnail: assignee.thumbnail,
        }
      : null,
    customer: {
      name: sender.name,
      email: sender.email,
      phoneNumber: sender.phone_number,
      thumbnail: sender.thumbnail,
      company: sender.additional_attributes?.company_name || null,
    },
  };
};

/**
 * Whether a presented row matches the table's search box.
 *
 * The search narrows the rows already loaded, over the text the table shows:
 * who the row is about, what it is about, and its id.
 *
 * @param {Object} row - a row from `presentTicket`
 * @param {string} query - the raw search input
 * @returns {boolean}
 */
export const rowMatchesSearch = (row, query) => {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  return [
    row.customer.name,
    row.customer.email,
    row.customer.company,
    row.subject,
    row.preview,
    row.assignee?.name,
    `#${row.id}`,
  ].some(value => value?.toLowerCase().includes(term));
};
